import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Collapse,
  Container,
  IconButton,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import { useDebounce } from 'use-debounce';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { t } from 'i18next';
import { ApiCustomDop } from '../../../api/hexa/ApiCustomDop';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { Add } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { HexaCustomDop } from '../../../api/hexa/models/CustomDopModels';
import { BreadcrumbsTitleContext } from '../../../wrappers/AppWrapper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const CustomDopList = () => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<any[]>([]);
  const [rows, setRows] = useState<HexaCustomDop[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const { setCustomDopFileName } = useContext(BreadcrumbsTitleContext);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [searchFullText, setSearchFullText] = useState<string>('');

  const [debounceSearchFullText] = useDebounce(searchFullText, 500);
  const [error, setError] = useState({
    open: false,
    message: '',
  });

  const downloadFile = (data: string, fileName: string) => {
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.json`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const renderDownloadButton = (data: string) => {
    return (
      <>
        {data === '{}' && <Box sx={{ color: sgrColors.backdrop }}>No data</Box>}
        {data !== '{}' && (
          <Tooltip title={t('custom_dop_list.download')}>
            <IconButton
              aria-label="download"
              color="primary"
              onClick={() => downloadFile(data, 'json_schema')}
            >
              <DownloadForOfflineIcon />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  const expandableCell = (params: any) => {
    const isString = typeof params.value === 'string';
    const isExpanded = expandedRows.includes(params.id);
    return (
      <Collapse
        in={isExpanded}
        collapsedSize={48}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Box
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: isExpanded ? 'unset' : 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: isExpanded ? 'unset' : 'ellipsis',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        >
          <Typography variant="body2">
            {isString ? params.value : params.value.join(', ')}
          </Typography>
        </Box>
      </Collapse>
    );
  };

  const columns: GridColDef<HexaCustomDop>[] = [
    {
      field: 'id',
      headerName: t('custom_dop_list.id'),
      flex: 1,
      maxWidth: 50,
    },
    {
      field: 'description',
      headerName: t('custom_dop_list.description'),
      flex: 1,
    },
    {
      field: 'file_name',
      headerName: t('custom_dop_list.file_name'),
      flex: 1.5,
      renderCell: (params: any) => expandableCell(params),
    },
    {
      field: 'json_schema',
      headerName: t('custom_dop_list.json_schema'),
      flex: 1,
      maxWidth: 110,
      align: 'center',
      renderCell: (params) =>
        renderDownloadButton(JSON.stringify(params.value)),
    },
    {
      field: 'delta',
      headerName: t('custom_dop_list.delta'),
      flex: 1,
      type: 'boolean',
      maxWidth: 50,
    },
    {
      field: 'interest_areas',
      headerName: t('custom_dop_list.interest_areas'),
      flex: 1,
      renderCell: (params: any) => expandableCell(params),
    },
    {
      field: 'sub_tags',
      headerName: t('custom_dop_list.sub_tags'),
      flex: 1,
      align: 'center',
      maxWidth: 75,
      renderCell: (params) =>
        renderDownloadButton(JSON.stringify(params.value)),
    },
    {
      field: 'dop_type',
      headerName: t('custom_dop_list.dop_type'),
      flex: 1,
      renderCell: (params: any) => expandableCell(params),
    },
    {
      field: 'entity_type',
      headerName: t('custom_dop_list.entity_type'),
      flex: 1,
      align: 'center',
      maxWidth: 90,
      renderCell: (params) =>
        renderDownloadButton(JSON.stringify(params.value)),
    },
    {
      field: 'enabled',
      headerName: t('custom_dop_list.enabled'),
      flex: 1,
      type: 'boolean',
      maxWidth: 70,
    },
    {
      field: 'execution_days',
      headerName: t('custom_dop_list.execution_days'),
      flex: 1,
      renderCell: (params: any) => expandableCell(params),
    },
    {
      field: 'actions',
      type: 'actions',
      maxWidth: 50,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title={t('custom_dop_list.expand')}>
              <ExpandMoreIcon color="primary" />
            </Tooltip>
          }
          onClick={() => {
            setExpandedRows((prev: number[]) => {
              return prev.includes(params.id as number)
                ? prev.filter((id: number) => id !== params.id)
                : [...prev, params.id];
            });
          }}
          label={t('custom_dop_list.expand')}
        />,
      ],
    },
  ];

  /**
   * Fetch the custom DOPs linked to the application from the API
   */
  const fetchCustomDops = useCallback(async () => {
    setLoading(true);
    try {
      const { total, custom_dop } = await ApiCustomDop.index({
        page: paginationModel.page,
        size: paginationModel.pageSize,
        search: debounceSearchFullText,
      });
      setRowCount(total);
      setRows(custom_dop);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching the custom DOP list: 
        ${error.response?.data.error ?? 'Error fetching the custom DOP list'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debounceSearchFullText]);

  useEffect(() => {
    fetchCustomDops();
  }, [fetchCustomDops]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  const handleRowClick = (params: GridRowParams) => {
    setCustomDopFileName(params.row.file_name);
    localStorage.setItem('customDopFileName', params.row.file_name);
    window.open(`${window.location.href}/${params.id.toString()}`);
  };

  return (
    <Container>
      <Card sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
        <Stack
          spacing={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              label="Search"
              variant="outlined"
              fullWidth
              size="small"
              value={searchFullText}
              sx={{ backgroundColor: 'white' }}
              onChange={(e) => setSearchFullText(e.target.value)}
            />
            <IconButton
              onClick={() => navigate('new')}
              sx={{
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  color: sgrColors.azure,
                  backgroundColor: 'transparent',
                  transform: 'scale(1.2)',
                },
              }}
            >
              <Add />
            </IconButton>
          </Stack>
          <DataGridPremium
            getRowId={(row) => row.id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            paginationMode="server"
            pagination={true}
            pageSizeOptions={[10, 25, 50, 100]}
            autoHeight={true}
            rows={rows}
            columns={columns}
            density={'compact'}
            onRowClick={handleRowClick}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
            getRowHeight={() => 'auto'}
            detailPanelExpandedRowIds={expandedRows}
          />
        </Stack>
        <Snackbar
          open={error.open}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error">
            {error.message}
          </Alert>
        </Snackbar>
      </Card>
    </Container>
  );
};

export default CustomDopList;
